import { useState } from "react"

import { Check, ClipboardCopy, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"

type AIResultDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  isLoading: boolean
  result: string | null
  onRegenerate: () => void
  onAccept?: (text: string) => void
}

export function AIResultDialog({
  open,
  onOpenChange,
  title,
  isLoading,
  result,
  onRegenerate,
  onAccept,
}: AIResultDialogProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleAccept = () => {
    if (result && onAccept) {
      onAccept(result)
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Spinner className="mr-2" />
              <span className="text-muted-foreground text-sm">Generating...</span>
            </div>
          )}
          {!isLoading && result && (
            <div className="prose dark:prose-invert max-w-none text-sm whitespace-pre-wrap">
              {result}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button disabled={isLoading} size="sm" variant="outline" onClick={onRegenerate}>
            <RefreshCw className="mr-1 size-4" />
            Regenerate
          </Button>
          <Button disabled={isLoading || !result} size="sm" variant="outline" onClick={handleCopy}>
            {copied ? <Check className="mr-1 size-4" /> : <ClipboardCopy className="mr-1 size-4" />}
            {copied ? "Copied" : "Copy"}
          </Button>
          {onAccept && (
            <Button disabled={isLoading || !result} size="sm" onClick={handleAccept}>
              Accept
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
